/**
 * @file components/dispatch/application-details-form.ts
 * @description 案件「申請明細」表單值 ⇄ applicationDetails 的純函式轉換（新建 / 修改案件共用，便於單測）。
 *
 * 背景（2026-09 客戶回報清單）：
 * - 序6：有機【驗證範圍】改為自由文字多行輸入（取代逐筆勾選作物字典），使用者直接輸入
 *   最終核定的產品範圍，如【大漿果】→「香蕉、芭蕉」。
 * - 序7：系統比對作物管理中該【驗證品項】底下尚未建立的範圍，提示派案人員確認是否新增。
 * - 序14：修改案件的申請明細須與新建案件同一格式——兩處共用本檔與 ApplicationDetailsFields。
 * - 序16：產銷履歷申請明細解鎖為非必填。
 *
 * 落庫格式不變（不動 jsonb schema 結構）：
 * - productItems：`{ name: 驗證品項 }`
 * - productScopes：`{ name: "驗證品項[範圍1、範圍2]" }`（無品項時僅存範圍文字）
 */

import {
  isTrackingVerificationType,
  sumTapArea,
  toAreaString,
  type CaseProductEntry,
  type TapApplicationRow,
} from '@zhcb/shared';
import {
  findTopItemKeyByName,
  toTopItemKey,
  topItemNameFromKey,
  type TopCropLike,
} from '@/components/dispatch/top-crop-options';

// ─── 表單值型別 ────────────────────────────────────────────────────────────────

/** 有機 TOP「驗證品項＋驗證範圍（自由文字）」一組的表單值 */
export interface TopProductGroupValue {
  /** 驗證品項鍵（`類別::品項`；存量資料對不上字典時為品項原文） */
  itemKey?: string;
  /** 驗證範圍自由文字（多項以「、」或換行分隔） */
  scopeText?: string;
  /** 本組附件（files 表 ID） */
  attachmentFileIds?: number[];
}

/** 產銷履歷 TAP 明細行表單值（appliedArea 為 InputNumber stringMode 字串） */
export interface TapRowValue {
  /** 作物字典 ID；產品類別/品項由選中作物帶入 */
  cropId?: number;
  productCategory?: string;
  productItem?: string;
  cropPeriod?: string;
  appliedArea?: string;
  reasonableYield?: string;
  attachmentFileIds?: number[];
}

/** 申請明細區塊的表單值（新建 / 修改案件表單共用的欄位子集） */
export interface ApplicationDetailsFormValues {
  topProductGroups?: TopProductGroupValue[];
  /** 申請驗證面積（初次申請 / 增列查驗） */
  topApplyAreaHa?: number;
  /** 有機面積（追蹤 / 展延查驗） */
  topOrganicAreaHa?: number;
  /** 有機轉型期面積（追蹤 / 展延查驗） */
  topOrganicConversionAreaHa?: number;
  tapRows?: TapRowValue[];
  tapLandCount?: number;
  tapSiteCount?: number;
}

// ─── 驗證範圍自由文字 ──────────────────────────────────────────────────────────

/** 驗證範圍分隔符：頓號、全/半形逗號、全/半形分號、換行 */
const SCOPE_SEPARATOR_RE = /[、，,；;\r\n]+/;

/**
 * 將驗證範圍自由文字拆成範圍名稱陣列（去空白、去重、保留輸入順序）。
 *
 * @example splitScopeText('香蕉、芭蕉\n香蕉') → ['香蕉', '芭蕉']
 */
export function splitScopeText(text: string | null | undefined): string[] {
  if (!text) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of text.split(SCOPE_SEPARATOR_RE)) {
    const name = part.trim();
    if (name && !seen.has(name)) {
      seen.add(name);
      out.push(name);
    }
  }
  return out;
}

// ─── TOP：表單值 → productItems / productScopes ───────────────────────────────

/**
 * 依「驗證品項＋驗證範圍文字」組出落庫的 productItems / productScopes。
 * 品項與範圍皆空的組直接略過；只有範圍沒有品項時，範圍仍保留（不帶品項前綴）避免資料遺失。
 */
export function buildTopProductEntries(groups: readonly TopProductGroupValue[] | undefined): {
  productItems: CaseProductEntry[];
  productScopes: CaseProductEntry[];
} {
  const productItems: CaseProductEntry[] = [];
  const productScopes: CaseProductEntry[] = [];
  for (const g of groups ?? []) {
    const itemName = g.itemKey?.trim() ? topItemNameFromKey(g.itemKey.trim()) : '';
    const scopes = splitScopeText(g.scopeText);
    const attachmentFileIds = g.attachmentFileIds?.length ? g.attachmentFileIds : undefined;
    if (!itemName && !scopes.length) continue;
    if (itemName) productItems.push({ name: itemName, attachmentFileIds });
    if (scopes.length) {
      productScopes.push({
        name: itemName ? `${itemName}[${scopes.join('、')}]` : scopes.join('、'),
        // 附件已掛在品項上時不重複掛到範圍，避免下游重複列出同一附件
        attachmentFileIds: itemName ? undefined : attachmentFileIds,
      });
    }
  }
  return { productItems, productScopes };
}

// ─── TOP：落庫值 → 表單值（修改案件預填） ─────────────────────────────────────

/** 解析 `品項[範圍1、範圍2]` 格式；不符合格式回 null */
function parseScopeEntryName(name: string): { itemName: string; scopes: string[] } | null {
  const m = /^(.+?)\[(.*)\]$/s.exec(name.trim());
  if (!m) return null;
  return { itemName: m[1]!.trim(), scopes: splitScopeText(m[2]) };
}

/**
 * 由既有案件的 productItems / productScopes 還原「驗證品項＋驗證範圍文字」分組。
 *
 * - `品項[範圍…]` 格式的範圍配回同名品項；
 * - 無品項前綴的存量範圍（舊版兩份獨立清單的寫法）：只有一個品項時併入該品項，
 *   否則獨立成一組（品項留空），確保修改時不遺失任何範圍文字；
 * - 品項名稱能對上作物字典時轉成下拉鍵，對不上時保留原文（下拉會以原文顯示）。
 */
export function parseTopProductGroups(
  productItems: readonly CaseProductEntry[] | undefined,
  productScopes: readonly CaseProductEntry[] | undefined,
  crops: readonly TopCropLike[],
): TopProductGroupValue[] {
  const groups: (TopProductGroupValue & { itemName: string; scopes: string[] })[] = (
    productItems ?? []
  ).map((it) => ({
    itemName: it.name,
    itemKey: findTopItemKeyByName(crops, it.name) ?? it.name,
    scopes: [],
    attachmentFileIds: it.attachmentFileIds,
  }));
  const orphanScopes: string[] = [];

  for (const sc of productScopes ?? []) {
    const parsed = parseScopeEntryName(sc.name);
    if (parsed) {
      const hit = groups.find((g) => g.itemName === parsed.itemName);
      if (hit) {
        hit.scopes.push(...parsed.scopes);
      } else {
        groups.push({
          itemName: parsed.itemName,
          itemKey: findTopItemKeyByName(crops, parsed.itemName) ?? parsed.itemName,
          scopes: parsed.scopes,
          attachmentFileIds: sc.attachmentFileIds,
        });
      }
    } else {
      orphanScopes.push(...splitScopeText(sc.name));
    }
  }

  if (orphanScopes.length) {
    if (groups.length === 1) groups[0]!.scopes.push(...orphanScopes);
    else groups.push({ itemName: '', itemKey: undefined, scopes: orphanScopes });
  }

  return groups.map((g) => ({
    itemKey: g.itemKey,
    scopeText: splitScopeText(g.scopes.join('、')).join('、') || undefined,
    attachmentFileIds: g.attachmentFileIds,
  }));
}

// ─── TOP：比對作物管理，找出尚未建立的驗證範圍（序7） ───────────────────────────

/** 作物管理中尚未建立於某驗證品項下的驗證範圍 */
export interface MissingTopScope {
  /** 有機主要類別（有機農糧產品 / 有機加工品） */
  mainCategory: string;
  /** 驗證品項（字典 subCategory） */
  subCategory: string;
  /** 尚未建立的驗證範圍名稱 */
  name: string;
}

/**
 * 找出各組驗證範圍中，作物管理「該驗證品項」底下尚未建立的名稱。
 * 品項對不上字典（存量原文 / 未選品項）的組不比對——無從得知應新增到哪個品項。
 * 跨組重複的缺漏只回傳一次。
 */
export function findMissingTopScopes(
  groups: readonly TopProductGroupValue[] | undefined,
  crops: readonly TopCropLike[],
): MissingTopScope[] {
  const out: MissingTopScope[] = [];
  const seen = new Set<string>();
  for (const g of groups ?? []) {
    const itemKey = g.itemKey?.trim();
    if (!itemKey) continue;
    const itemCrops = crops.filter((c) => toTopItemKey(c.mainCategory, c.subCategory) === itemKey);
    // 品項不在字典中（存量原文）→ 不比對
    if (!itemCrops.length) continue;
    const existing = new Set(itemCrops.map((c) => c.name.trim()));
    const { mainCategory, subCategory } = itemCrops[0]!;
    for (const name of splitScopeText(g.scopeText)) {
      const key = `${itemKey}|${name}`;
      if (existing.has(name) || seen.has(key)) continue;
      seen.add(key);
      out.push({ mainCategory, subCategory, name });
    }
  }
  return out;
}

// ─── TOP：驗證面積（序165 依驗證服務類型分展示） ────────────────────────────────

/**
 * 組出 TOP 面積欄位：追蹤/展延型以「有機面積＋轉型期面積」合計、其餘以申請驗證面積，
 * 統一寫回 verificationArea（下游證書資訊讀單一總值），並保留三個分項欄位。
 */
export function buildTopAreaFields(
  values: Pick<
    ApplicationDetailsFormValues,
    'topApplyAreaHa' | 'topOrganicAreaHa' | 'topOrganicConversionAreaHa'
  >,
  verificationTypes: readonly string[] | undefined,
): {
  verificationArea?: string;
  applyAreaHa?: number;
  organicAreaHa?: number;
  organicConversionAreaHa?: number;
} {
  const trackingTotal = (values.topOrganicAreaHa ?? 0) + (values.topOrganicConversionAreaHa ?? 0);
  const effective = isTrackingVerificationType(verificationTypes)
    ? trackingTotal
    : (values.topApplyAreaHa ?? 0);
  return {
    verificationArea: effective > 0 ? toAreaString(effective) : undefined,
    applyAreaHa: values.topApplyAreaHa ?? undefined,
    organicAreaHa: values.topOrganicAreaHa ?? undefined,
    organicConversionAreaHa: values.topOrganicConversionAreaHa ?? undefined,
  };
}

// ─── TAP：表單值 → rows（序16 非必填） ─────────────────────────────────────────

/** 判斷 TAP 明細行除作物外是否填了任何內容 */
export function tapRowHasContent(row: TapRowValue | undefined): boolean {
  if (!row) return false;
  return !!(
    row.cropPeriod?.trim() ||
    (row.appliedArea !== undefined && row.appliedArea !== null && String(row.appliedArea) !== '') ||
    row.reasonableYield?.trim() ||
    row.attachmentFileIds?.length
  );
}

/**
 * 組出 TAP 明細 rows：未選作物的行一律略過（非必填，空行不送出）；
 * 表單層另以校驗阻止「填了其他欄位卻未選作物」的行被靜默丟棄。
 */
export function buildTapRows(rows: readonly TapRowValue[] | undefined): TapApplicationRow[] {
  return (rows ?? [])
    .filter((r) => !!r?.cropId && !!r.productCategory?.trim() && !!r.productItem?.trim())
    .map((r) => ({
      cropId: r.cropId,
      productCategory: r.productCategory!.trim(),
      productItem: r.productItem!.trim(),
      cropPeriod: r.cropPeriod?.trim() || undefined,
      appliedArea: toAreaString(r.appliedArea),
      reasonableYield: r.reasonableYield?.trim() || undefined,
      attachmentFileIds: r.attachmentFileIds?.length ? r.attachmentFileIds : undefined,
    }));
}

/** TAP 驗證總面積（各行申請面積加總，4 位小數字串） */
export function tapTotalAreaOf(rows: readonly TapRowValue[] | undefined): string {
  return sumTapArea((rows ?? []).map((r) => ({ appliedArea: r?.appliedArea })));
}
